import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { Eye, EyeOff, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const from = location.state?.from?.pathname || localStorage.getItem('returnAfterAuth');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationPrompt(false);

    try {
      const result = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      localStorage.removeItem('returnAfterAuth');
      
      // Role-based redirect:
      // - Admin goes to admin panel
      // - Host goes to dashboard
      // - Regular user goes to profile
      let redirectTo = from;
      if (!redirectTo) {
        if (result.user?.role === 'admin') {
          redirectTo = '/admin';
        } else if (result.user?.role === 'host') {
          redirectTo = '/dashboard';
        } else {
          redirectTo = '/profile';
        }
      }
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      
      // Check if the error is due to unverified email (403)
      if (status === 403 && message.toLowerCase().includes('verify')) {
        setShowVerificationPrompt(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {
        email: formData.email
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4EDE4] flex flex-col" data-testid="login-page">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b py-4 px-6">
        <Logo size={34} />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-2xl font-semibold text-center mb-2">Welcome back</h1>
            <p className="text-gray-500 text-center mb-8">Log in to manage your experiences</p>

            {/* Verification Required Alert */}
            {showVerificationPrompt && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="verification-alert">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 mb-1">Email verification required</p>
                    <p className="text-sm text-amber-700 mb-3">
                      Please verify your email address before logging in. Check your inbox for the verification link.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendLoading || !formData.email}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                      data-testid="resend-verification-btn"
                    >
                      {resendLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" /> Resend verification email</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="mt-2"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white py-6 rounded-lg font-semibold"
                data-testid="login-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Logging in...</>
                ) : (
                  'Log in'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-[#D65A31] hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
              <p className="text-gray-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#D65A31] font-medium hover:underline" data-testid="register-link">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
